import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { httpsCallable } from "firebase/functions";
import { collection, query, where, limit, getDocs, doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { Timestamp } from "firebase/firestore";
import { functions, db } from "../firebase";
import { useAuth } from "./AuthContext";
import { logger } from "../lib/logger";

export type FirmRole = "OWNER" | "ADMIN" | "ACCOUNTANT" | "MEMBER" | "PARALEGAL" | "READ_ONLY" | null;

interface FirmContextType {
  /** The firm's Firestore document ID. Falls back to user.uid during bootstrap. */
  firmId: string | null;
  /** The current user's role within the firm. */
  role: FirmRole;
  /** True once firmId is resolved (either from claims or after bootstrap). */
  firmReady: boolean;
  /** True if this user has legitimate access (existing firm claims OR accepted invite). False for uninvited new signups. */
  hasAccess: boolean;
  /** Convenience: true if user is the firm owner. */
  isOwner: boolean;
  /** Convenience: true if user is OWNER or ADMIN. */
  isAdmin: boolean;
  /** Convenience: true if any firm role is set. */
  isMember: boolean;
  /** Convenience: true if role can trigger credit-gated AI operations (all roles except READ_ONLY). */
  canRunAI: boolean;
  /** True once the firm owner has completed the onboarding wizard. Legacy firms without the field are treated as completed. */
  onboardingComplete: boolean;
  /** TASK-1-10 — True once every Gate-1 mandatory firm-setup field is populated. Legacy
   *  firms without the field are treated as INCOMPLETE so the strict Gate-1 contract holds. */
  setupComplete: boolean;
  /** The date the 14-day trial expires. Null for legacy/paid firms. */
  trialExpiresAt: Date | null;
  /** Subscription status from Stripe: 'active' | 'cancelled' | null (trial/unknown). */
  subscriptionStatus: string | null;
  /** Raw firm status from the firm document, e.g. 'active' | 'suspended' | 'pending'. */
  firmStatus: string | null;
  /** True if the firm's `status` field is "suspended" (set by super admin). */
  firmSuspended: boolean;
  /** Re-read firm onboarding and trial metadata from Firestore. */
  refreshFirmMeta: () => Promise<void>;
  /** Dev-only role override setter */
  setRoleOverride?: (role: FirmRole) => void;
}

const FirmContext = createContext<FirmContextType>({
  firmId: null,
  role: null,
  firmReady: false,
  hasAccess: true,
  isOwner: false,
  isAdmin: false,
  isMember: false,
  canRunAI: false,
  onboardingComplete: true,
  setupComplete: false,
  trialExpiresAt: null,
  subscriptionStatus: null,
  firmStatus: null,
  firmSuspended: false,
  refreshFirmMeta: async () => {},
  setRoleOverride: () => {},
});

const USE_ROLE_OVERRIDE =
  import.meta.env.VITE_USE_MOCK_DATA === "1" &&
  (import.meta.env.PROD !== true || import.meta.env.VITE_ALLOW_PRODUCTION_MOCK_DATA === "1");

type InviteAcceptanceResult = {
  accepted: boolean;
  firmId: string | null;
  role: FirmRole;
};

async function checkAndAcceptPendingInviteWithClaims(userEmail: string): Promise<InviteAcceptanceResult> {
  try {
    const invitesSnap = await getDocs(query(
      collection(db, "firm_invites"),
      where("email", "==", userEmail.toLowerCase()),
      where("status", "==", "pending"),
      limit(1)
    ));
    if (invitesSnap.empty) return { accepted: false, firmId: null, role: null };

    const firstDoc = invitesSnap.docs[0];
    if (!firstDoc) return { accepted: false, firmId: null, role: null };
    const token = firstDoc.id;

    const acceptInvite = httpsCallable<{ token: string }, { success: boolean; firmId?: string; role?: FirmRole }>(
      functions, "acceptFirmInvite"
    );
    const res = await acceptInvite({ token });
    if (res.data.success !== true) {
      return { accepted: false, firmId: null, role: null };
    }
    return {
      accepted: true,
      firmId: typeof res.data.firmId === "string" ? res.data.firmId : null,
      role: (res.data.role as FirmRole | undefined) ?? null,
    };
  } catch {
    // Non-fatal: if invite check fails, fall through to bootstrap
    return { accepted: false, firmId: null, role: null };
  }
}

/** Read the firm doc to get onboarding + trial + subscription + status fields. Safe to call after firmId is known. */
async function readFirmMeta(firmId: string): Promise<{ onboardingComplete: boolean; setupComplete: boolean; trialExpiresAt: Date | null; subscriptionStatus: string | null; firmStatus: string | null }> {
  try {
    const snap = await getDoc(doc(db, "firms", firmId));
    if (!snap.exists()) return { onboardingComplete: true, setupComplete: false, trialExpiresAt: null, subscriptionStatus: null, firmStatus: null };
    const data = snap.data();
    const onboardingComplete = data.onboardingComplete === false ? false : true;
    // TASK-1-10 — strict matrix interpretation: missing setupComplete ⇒ treat as false.
    // Downstream callables and Firestore rules will block client/case/contract creation
    // until the firm owner re-runs the onboarding wizard with all Gate-1 fields filled.
    const setupComplete = data.setupComplete === true;
    // CO-04: For legacy firms onboarded before termsAcceptedAt existed, set it if missing
    if (onboardingComplete && !data.termsAcceptedAt) {
      try {
        await updateDoc(doc(db, "firms", firmId), { termsAcceptedAt: serverTimestamp() });
      } catch {
        // Non-blocking: termsAcceptedAt write failure should not block login
      }
    }
    const rawExpiry = data.trialExpiresAt;
    const trialExpiresAt = rawExpiry instanceof Timestamp ? rawExpiry.toDate() : null;
    const subscriptionStatus = (data.subscriptionStatus as string | undefined) ?? null;
    const firmStatus = (data.status as string | undefined) ?? null;
    return { onboardingComplete, setupComplete, trialExpiresAt, subscriptionStatus, firmStatus };
  } catch {
    return { onboardingComplete: true, setupComplete: false, trialExpiresAt: null, subscriptionStatus: null, firmStatus: null };
  }
}

export function FirmProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthReady } = useAuth();
  const [firmId, setFirmId] = useState<string | null>(null);
  const [role, setRole] = useState<FirmRole>(null);
  const [roleOverride, setRoleOverrideState] = useState<FirmRole>(() => {
    if (!USE_ROLE_OVERRIDE) return null;
    if (typeof window !== "undefined") {
      return (localStorage.getItem("iamv:dev-role-override") as FirmRole) || null;
    }
    return null;
  });

  const setRoleOverride = useCallback((newRole: FirmRole) => {
    if (!USE_ROLE_OVERRIDE) return;
    if (typeof window !== "undefined") {
      if (newRole) {
        localStorage.setItem("iamv:dev-role-override", newRole);
      } else {
        localStorage.removeItem("iamv:dev-role-override");
      }
      setRoleOverrideState(newRole);
    }
  }, []);

  useEffect(() => {
    if (!USE_ROLE_OVERRIDE && typeof window !== "undefined") {
      localStorage.removeItem("iamv:dev-role-override");
    }
  }, []);

  const [firmReady, setFirmReady] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);
  const [onboardingComplete, setOnboardingComplete] = useState(true);
  const [setupComplete, setSetupComplete] = useState(false);
  const [trialExpiresAt, setTrialExpiresAt] = useState<Date | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [firmStatus, setFirmStatus] = useState<string | null>(null);
  const [firmSuspended, setFirmSuspended] = useState(false);
  const bootstrapCalledRef = useRef(false);

  const refreshFirmMeta = useCallback(async () => {
    if (!firmId) return;
    const meta = await readFirmMeta(firmId);
    setOnboardingComplete(meta.onboardingComplete);
    setSetupComplete(meta.setupComplete);
    setTrialExpiresAt(meta.trialExpiresAt);
    setSubscriptionStatus(meta.subscriptionStatus);
    setFirmStatus(meta.firmStatus);
    setFirmSuspended(meta.firmStatus === "suspended");
  }, [firmId]);

  const refreshClaimsWithRetry = useCallback(async () => {
    if (!user) return { firmId: null as string | null, role: null as FirmRole };

    for (let attempt = 0; attempt < 4; attempt += 1) {
      await user.getIdToken(true);
      const refreshed = await user.getIdTokenResult(false);
      const refreshedClaims = refreshed.claims as Record<string, unknown>;
      const refreshedFirmId = typeof refreshedClaims.firmId === "string" ? refreshedClaims.firmId : null;
      const refreshedRole = (refreshedClaims.role as FirmRole | undefined) ?? null;

      if (refreshedFirmId && refreshedRole) {
        return { firmId: refreshedFirmId, role: refreshedRole };
      }

      await new Promise((resolve) => setTimeout(resolve, 350));
    }

    return { firmId: null as string | null, role: null as FirmRole };
  }, [user]);

  const resolveFirmIdentity = useCallback(async () => {
    if (!user) {
      setFirmId(null);
      setRole(null);
      setFirmReady(false);
      setHasAccess(true);
      bootstrapCalledRef.current = false;
      return;
    }

    try {
      // Read claims from the current JWT — intentionally NOT force-refreshing here because
      // a forced token refresh restarts all Firestore watch streams via onCredentialChange,
      // which collides with in-flight permission-denied errors and triggers
      // INTERNAL ASSERTION FAILED crashes. Token is refreshed forcefully only when needed.
      const idTokenResult = await user.getIdTokenResult(false);
      const claims = idTokenResult.claims as Record<string, unknown>;

      const claimFirmId = claims.firmId as string | undefined;
      const claimRole = claims.role as FirmRole | undefined;

      if (claimFirmId && claimRole) {
        // JWT has firm claims — use them directly
        setFirmId(claimFirmId);
        setRole(claimRole);
        const meta = await readFirmMeta(claimFirmId);
        setOnboardingComplete(meta.onboardingComplete);
        setSetupComplete(meta.setupComplete);
        setTrialExpiresAt(meta.trialExpiresAt);
        setSubscriptionStatus(meta.subscriptionStatus);
        setFirmStatus(meta.firmStatus);
        setFirmSuspended(meta.firmStatus === "suspended");

        // RBAC-GUARD: For non-OWNER roles, verify the `perms` JWT claim is populated.
        // If it's missing or empty, the syncMemberPermissionsToJWT trigger may not have
        // fired yet (e.g. new member, stale session). Force-refresh once to pick up claims
        // written by the backfill script or the trigger. OWNER is exempt — the rules
        // short-circuit for OWNER before reading the perms map.
        if (claimRole !== "OWNER") {
          const perms = claims.perms as Record<string, boolean> | undefined;
          const hasPerms = perms && Object.keys(perms).length > 0;
          if (!hasPerms) {
            logger.warn("FirmContext: perms claim missing for non-OWNER — force-refreshing token", {
              uid: user.uid,
              firmId: claimFirmId,
              role: claimRole,
            });
            try {
              // Force a single token refresh so the newly-written perms claim propagates.
              // We intentionally don't call refreshClaimsWithRetry() here to avoid
              // restarting all Firestore watch streams unnecessarily.
              await user.getIdToken(true);
            } catch (refreshErr) {
              logger.warn("FirmContext: token force-refresh failed (non-fatal)", { refreshErr });
            }
          }
        }

        setFirmReady(true);
        return;
      }

      // No firm claims — first check if there's a pending invite for this user
      if (!bootstrapCalledRef.current) {
        bootstrapCalledRef.current = true;

        const userEmail = user.email ?? "";
        if (userEmail) {
          const inviteResult = await checkAndAcceptPendingInviteWithClaims(userEmail);
          if (inviteResult.accepted) {
            // Invite was accepted — block firm listener mount until claims are observable.
            const refreshedClaims = await refreshClaimsWithRetry();
            const newFirmId = refreshedClaims.firmId ?? inviteResult.firmId;
            const newRole = refreshedClaims.role ?? inviteResult.role;
            if (newFirmId && newRole) {
              setFirmId(newFirmId);
              setRole(newRole);
              const meta = await readFirmMeta(newFirmId);
              setOnboardingComplete(meta.onboardingComplete);
              setSetupComplete(meta.setupComplete);
              setTrialExpiresAt(meta.trialExpiresAt);
              setSubscriptionStatus(meta.subscriptionStatus);
              setFirmStatus(meta.firmStatus);
              setFirmSuspended(meta.firmStatus === "suspended");
              setFirmReady(true);
              return;
            }
          }
        }

        // No pending invite — check if this is a super admin (allowed to bootstrap)
        const isSuperAdminUser = (claims.superAdmin as boolean | undefined) === true;

        if (!isSuperAdminUser) {
          // Uninvited regular user — deny access without creating a firm
          setFirmId(null);
          setRole(null);
          setHasAccess(false);
          setFirmReady(true);
          return;
        }

        // Super admin: bootstrap their own firm (creates 1:1 firm + sets claims)
        try {
          const bootstrap = httpsCallable<void, { firmId: string; role: FirmRole }>(
            functions, "bootstrapFirm"
          );
          const result = await bootstrap();
          const refreshedClaims = await refreshClaimsWithRetry();
          const newFirmId = refreshedClaims.firmId ?? result.data.firmId;
          const newRole = refreshedClaims.role ?? result.data.role;
          setFirmId(newFirmId);
          setRole(newRole);
          const meta = await readFirmMeta(newFirmId);
          setOnboardingComplete(meta.onboardingComplete);
          setSetupComplete(meta.setupComplete);
          setTrialExpiresAt(meta.trialExpiresAt);
          setSubscriptionStatus(meta.subscriptionStatus);
          setFirmStatus(meta.firmStatus);
          setFirmSuspended(meta.firmStatus === "suspended");
        } catch (err) {
          logger.warn("[FirmContext] bootstrapFirm failed — using uid fallback");
          // Keep the uid fallback set above
        }

        setFirmReady(true);
      }
    } catch (err) {
      logger.error("[FirmContext] resolveFirmIdentity failed:", { error: err });
      setRole(null);
      setHasAccess(false);
      setFirmId(null);
      setFirmReady(true);
    }
  }, [refreshClaimsWithRetry, user]);

  useEffect(() => {
    if (!isAuthReady) return;
    resolveFirmIdentity();
  }, [isAuthReady, resolveFirmIdentity]);

  const activeRole = roleOverride || role;
  const isOwner = activeRole === "OWNER";
  const isAdmin = activeRole === "OWNER" || activeRole === "ADMIN";
  const isMember = activeRole !== null;
  const canRunAI = activeRole !== null && activeRole !== "READ_ONLY";

  return (
    <FirmContext.Provider value={{
      firmId, role: activeRole, firmReady, hasAccess, isOwner, isAdmin, isMember, canRunAI,
      onboardingComplete, setupComplete, trialExpiresAt, subscriptionStatus, firmStatus, firmSuspended, refreshFirmMeta,
      setRoleOverride,
    }}>
      {children}
    </FirmContext.Provider>
  );
}

export const useFirm = () => useContext(FirmContext);
